import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useI18n, LANGS } from "@/context/i18n";
import { Logo } from "@/components/Logo";
import { User, LogOut, Briefcase, Globe, Hotel, Car, Ticket, Plane, Phone, Bell, Landmark } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const NAV = [
  { to: "/results/hotels?destination=IST", label: "hotels", Icon: Hotel, id: "hotels" },
  { to: "/results/transfers?destination=IST", label: "transfers", Icon: Car, id: "transfers" },
  { to: "/results/activities?destination=IST", label: "activities", Icon: Ticket, id: "activities" },
  { to: "/results/flights?origin=AYT&destination=IST", label: "flights", Icon: Plane, id: "flights" },
];

export const Header = () => {
  const { user, currency, changeCurrency, logout } = useApp();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();

  const login = () => {
    sessionStorage.setItem("postLoginRedirect", window.location.pathname + window.location.search);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <header data-testid="site-header" className="fixed top-0 left-0 right-0 z-[500] glass border-b border-[#E2E8F0]">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-[68px] flex items-center gap-4">
        <Link to="/" data-testid="logo-link" className="shrink-0"><Logo /></Link>

        <nav className="hidden lg:flex items-center gap-1 ml-6" data-testid="main-nav">
          {NAV.map(({ to, label, Icon, id }) => (
            <Link key={id} to={to} data-testid={`nav-${id}`}
              className="flex items-center gap-2 px-3.5 h-10 rounded-full text-sm font-semibold text-[#0B2038] hover:bg-[#0B2038]/6 hover:text-[#009E9E] transition-colors">
              <Icon className="w-4 h-4" strokeWidth={1.9} /> {t(`nav_${label}`)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 ml-auto">
          <a href="tel:+902121234567" data-testid="header-phone"
            className="hidden xl:flex items-center gap-2 text-sm font-semibold text-[#0B2038] px-3 h-9 rounded-full hover:bg-[#0B2038]/6 transition-colors">
            <Phone className="w-4 h-4 text-[#009E9E]" /> +90 212 123 45 67
          </a>

          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger data-testid="language-switcher" className="w-[70px] sm:w-[126px] h-9 rounded-full border-[#E2E8F0] text-sm font-semibold bg-white">
              <Globe className="w-4 h-4 text-[#009E9E] shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white z-[600]">
              {LANGS.map((l) => (
                <SelectItem key={l.code} value={l.code} data-testid={`lang-${l.code}`}>
                  <span className="mr-1">{l.flag}</span> {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={currency} onValueChange={changeCurrency}>
            <SelectTrigger data-testid="currency-switcher" className="w-[74px] h-9 rounded-full border-[#E2E8F0] text-sm font-semibold bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white z-[600]">
              <SelectItem value="EUR" data-testid="currency-EUR">EUR €</SelectItem>
              <SelectItem value="USD" data-testid="currency-USD">USD $</SelectItem>
              <SelectItem value="TRY" data-testid="currency-TRY">TRY ₺</SelectItem>
            </SelectContent>
          </Select>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-button" className="flex items-center gap-2 rounded-full border border-[#E2E8F0] pl-1 pr-3 h-9 hover:border-[#0B2038] transition-colors bg-white">
                  {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                    : <span className="w-7 h-7 rounded-full bg-[#0B2038] text-white flex items-center justify-center text-xs">{(user.name || "U")[0]}</span>}
                  <span className="text-sm font-semibold text-[#0B2038] hidden md:block max-w-[90px] truncate">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white z-[600]">
                <DropdownMenuItem data-testid="menu-my-trips" onClick={() => navigate("/trips")}>
                  <Briefcase className="w-4 h-4 mr-2" /> {t("trips")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-watchlist" onClick={() => navigate("/watchlist")}>
                  <Bell className="w-4 h-4 mr-2" /> {t("watchlist")}
                </DropdownMenuItem>
                {user.isAdmin && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <Landmark className="w-4 h-4 mr-2" /> {t("admin")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                  <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button data-testid="login-button" onClick={login}
              className="h-9 px-4 rounded-full bg-[#0B2038] text-white text-sm font-semibold hover:bg-[#132f52] transition-colors flex items-center gap-2">
              <User className="w-4 h-4" /> <span className="hidden sm:inline">{t("signin")}</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
